import type { GradedWork } from '../types';

export type AuditRowState = 'error' | 'draft' | 'ready';

export function getAuditRowState(w: GradedWork): AuditRowState {
  if (!w.studentId || !w.assignmentId || !w.courseId) return 'error';
  if (w.status === 'synced') return 'ready';
  const hasScore = typeof w.score === 'number' && !Number.isNaN(w.score);
  const hasFeedback = (w.feedback || '').trim().length > 0;
  return hasScore && hasFeedback ? 'ready' : 'draft';
}

export type AuditVisibleRow = { w: GradedWork; idx: number; state: AuditRowState };

export function getAuditVisibleRows(
  gradedWorks: GradedWork[],
  auditFilter: 'all' | 'errors' | 'drafts' | 'ready'
): AuditVisibleRow[] {
  return gradedWorks
    .map((w, idx) => ({ w, idx, state: getAuditRowState(w) }))
    .filter(({ state }) => {
      if (auditFilter === 'all') return true;
      if (auditFilter === 'errors') return state === 'error';
      if (auditFilter === 'drafts') return state === 'draft';
      return state === 'ready';
    });
}

export function getAuditResolvedFocus(
  visible: AuditVisibleRow[],
  auditStudioFocusIdx: number | null,
  auditEditIndex: number | null
): number | null {
  const visibleIds = visible.map((v) => v.idx);
  if (auditStudioFocusIdx !== null && visibleIds.includes(auditStudioFocusIdx)) {
    return auditStudioFocusIdx;
  }
  if (auditEditIndex !== null && visibleIds.includes(auditEditIndex)) {
    return auditEditIndex;
  }
  return visibleIds[0] ?? null;
}

export function filterVisibleBySearch(visible: AuditVisibleRow[], q: string): AuditVisibleRow[] {
  const needle = q.trim().toLowerCase();
  if (!needle) return visible;
  return visible.filter(({ w }) => (w.studentName || '').toLowerCase().includes(needle));
}

export function countAuditStats(gradedWorks: GradedWork[]) {
  let draft = 0;
  let ready = 0;
  let error = 0;
  for (const w of gradedWorks) {
    const s = getAuditRowState(w);
    if (s === 'draft') draft += 1;
    else if (s === 'ready') ready += 1;
    else error += 1;
  }
  const total = gradedWorks.length;
  const gradedComplete = ready;
  const ungraded = draft + error;
  return { draft, ready, error, total, gradedComplete, ungraded };
}
